import { actions } from "@mixitone/actions";
import tauri from "./tauri";
import {
  GLOBAL_SEARCH_PER_TYPE_LIMIT,
  type GlobalSearchRankingOptions,
  type GlobalSearchRawRow,
  type GlobalSearchResult,
  rankGlobalSearchRows,
} from "./globalSearchTypes";

export interface GlobalSearchParams {
  search: string;
  includeFunding?: boolean;
  ranking?: GlobalSearchRankingOptions;
}

function escapeLikePattern(value: string): string {
  return value.replace(/[%_\\]/g, (char) => `\\${char}`);
}

/** SQLite rejects ORDER BY on UNION branches unless each branch is wrapped. */
function wrapLimitedSubquery(sql: string): string {
  return `SELECT * FROM (\n${sql.trim()}\n)`;
}

function buildSqliteSearchQuery(includeFunding: boolean): string {
  const clubFilter = "AND (? IS NULL OR {table}.club_id = ?)";

  const queries = [
    `
      SELECT
        cp.id,
        'club_player' AS type,
        cp.name AS title,
        COALESCE(cp.email, cp.phone, g.name) AS subtitle,
        cp.club_id,
        c.name AS club_name,
        NULL AS tournament_session_id,
        cp.name AS match_name,
        cp.email AS match_email,
        cp.phone AS match_phone
      FROM club_players cp
      JOIN clubs c ON c.id = cp.club_id
      LEFT JOIN groups g ON g.id = cp.group_id AND g.is_deleted = 'false'
      WHERE cp.is_deleted = 'false'
        AND c.is_deleted = 'false'
        ${clubFilter.replace("{table}", "cp")}
        AND (
          LOWER(cp.name) LIKE LOWER(?)
          OR LOWER(COALESCE(cp.email, '')) LIKE LOWER(?)
          OR LOWER(COALESCE(cp.phone, '')) LIKE LOWER(?)
          OR LOWER(COALESCE(g.name, '')) LIKE LOWER(?)
        )
      ORDER BY cp.name
      LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
    `,
    `
      SELECT
        g.id,
        'group' AS type,
        g.name AS title,
        c.name AS subtitle,
        g.club_id,
        c.name AS club_name,
        NULL AS tournament_session_id,
        g.name AS match_name,
        NULL AS match_email,
        NULL AS match_phone
      FROM groups g
      JOIN clubs c ON c.id = g.club_id
      WHERE g.is_deleted = 'false'
        AND c.is_deleted = 'false'
        ${clubFilter.replace("{table}", "g")}
        AND LOWER(g.name) LIKE LOWER(?)
      ORDER BY g.name
      LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
    `,
    `
      SELECT
        ts.id,
        'tournament_session' AS type,
        ts.name AS title,
        COALESCE(ts.description, c.name) AS subtitle,
        ts.club_id,
        c.name AS club_name,
        ts.id AS tournament_session_id,
        ts.name AS match_name,
        NULL AS match_email,
        NULL AS match_phone
      FROM tournament_sessions ts
      JOIN clubs c ON c.id = ts.club_id
      WHERE ts.is_deleted = 'false'
        AND c.is_deleted = 'false'
        ${clubFilter.replace("{table}", "ts")}
        AND (
          LOWER(ts.name) LIKE LOWER(?)
          OR LOWER(COALESCE(ts.description, '')) LIKE LOWER(?)
        )
      ORDER BY ts.name
      LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
    `,
    `
      SELECT
        tp.id,
        'tournament_player' AS type,
        tp.name AS title,
        COALESCE(tp.email, tp.mobile, ts.name) AS subtitle,
        tp.club_id,
        c.name AS club_name,
        tp.tournament_session_id,
        tp.name AS match_name,
        tp.email AS match_email,
        tp.mobile AS match_phone
      FROM tournament_players tp
      JOIN tournament_sessions ts ON ts.id = tp.tournament_session_id
      JOIN clubs c ON c.id = tp.club_id
      WHERE tp.is_deleted = 'false'
        AND ts.is_deleted = 'false'
        AND c.is_deleted = 'false'
        ${clubFilter.replace("{table}", "tp")}
        AND (
          LOWER(tp.name) LIKE LOWER(?)
          OR LOWER(COALESCE(tp.email, '')) LIKE LOWER(?)
          OR LOWER(COALESCE(tp.mobile, '')) LIKE LOWER(?)
        )
      ORDER BY tp.name
      LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
    `,
  ];

  if (includeFunding) {
    queries.push(
      `
        SELECT
          f.id,
          'funder' AS type,
          f.name AS title,
          COALESCE(f.contact_email, f.contact_phone, f.website, f.contact_name) AS subtitle,
          f.club_id,
          c.name AS club_name,
          NULL AS tournament_session_id,
          COALESCE(f.contact_name, f.name) AS match_name,
          f.contact_email AS match_email,
          f.contact_phone AS match_phone
        FROM funders f
        JOIN clubs c ON c.id = f.club_id
        WHERE f.is_deleted = 'false'
          AND c.is_deleted = 'false'
          ${clubFilter.replace("{table}", "f")}
          AND (
            LOWER(f.name) LIKE LOWER(?)
            OR LOWER(COALESCE(f.contact_name, '')) LIKE LOWER(?)
            OR LOWER(COALESCE(f.contact_email, '')) LIKE LOWER(?)
            OR LOWER(COALESCE(f.contact_phone, '')) LIKE LOWER(?)
            OR LOWER(COALESCE(f.website, '')) LIKE LOWER(?)
          )
        ORDER BY f.name
        LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
      `,
      `
        SELECT
          fa.id,
          'funding_appeal' AS type,
          fa.name AS title,
          c.name AS subtitle,
          fa.club_id,
          c.name AS club_name,
          fa.tournament_session_id,
          fa.name AS match_name,
          NULL AS match_email,
          NULL AS match_phone
        FROM funding_appeals fa
        JOIN clubs c ON c.id = fa.club_id
        WHERE fa.is_deleted = 'false'
          AND c.is_deleted = 'false'
          ${clubFilter.replace("{table}", "fa")}
          AND LOWER(fa.name) LIKE LOWER(?)
        ORDER BY fa.name
        LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
      `,
      `
        SELECT
          fo.id,
          'funding_outreach' AS type,
          fo.purpose_label AS title,
          COALESCE(f.name, fa.name, c.name) AS subtitle,
          fo.club_id,
          c.name AS club_name,
          NULL AS tournament_session_id,
          fo.purpose_label AS match_name,
          NULL AS match_email,
          NULL AS match_phone
        FROM funding_outreach fo
        JOIN clubs c ON c.id = fo.club_id
        JOIN funders f ON f.id = fo.funder_id
        LEFT JOIN funding_appeals fa ON fa.id = fo.funding_appeal_id
        WHERE fo.is_deleted = 'false'
          AND c.is_deleted = 'false'
          AND f.is_deleted = 'false'
          ${clubFilter.replace("{table}", "fo")}
          AND (
            LOWER(fo.purpose_label) LIKE LOWER(?)
            OR LOWER(f.name) LIKE LOWER(?)
            OR LOWER(COALESCE(fa.name, '')) LIKE LOWER(?)
          )
        ORDER BY fo.purpose_label
        LIMIT ${GLOBAL_SEARCH_PER_TYPE_LIMIT}
      `,
    );
  }

  return queries.map(wrapLimitedSubquery).join("\nUNION ALL\n");
}

function sqliteValuesForQuery(pattern: string, includeFunding: boolean): Array<string | null> {
  const clubId = null;
  const values: Array<string | null> = [];

  const pushClubScoped = (patternCount: number) => {
    values.push(clubId, clubId);
    for (let i = 0; i < patternCount; i++) {
      values.push(pattern);
    }
  };

  pushClubScoped(4);
  pushClubScoped(1);
  pushClubScoped(2);
  pushClubScoped(3);

  if (includeFunding) {
    pushClubScoped(5);
    pushClubScoped(1);
    pushClubScoped(3);
  }

  return values;
}

async function searchOnTauri(params: GlobalSearchParams): Promise<GlobalSearchResult[]> {
  const includeFunding = params.includeFunding ?? false;
  const pattern = `%${escapeLikePattern(params.search)}%`;
  const query = buildSqliteSearchQuery(includeFunding);
  const values = sqliteValuesForQuery(pattern, includeFunding);

  const rows = await tauri.invoke<GlobalSearchRawRow[]>("plugin:mixitonedb|execute", {
    query,
    values,
  });

  return rankGlobalSearchRows(rows, params.search, params.ranking);
}

const globalSearchAction = actions.globalSearch;

export async function globalSearch(params: GlobalSearchParams): Promise<GlobalSearchResult[]> {
  if (tauri.enabled) {
    return searchOnTauri(params);
  }

  const response = await globalSearchAction.search({
    search: params.search,
    includeFunding: params.includeFunding,
  });
  return rankGlobalSearchRows(response.results as GlobalSearchRawRow[], params.search, params.ranking);
}
